import { useCallback, useEffect, useMemo, useState } from 'react';

import { DiffView } from './DiffView';
import { JsonNode } from './JsonNode';
import { SearchPanel } from './SearchPanel';
import { useJsonEditor } from './useJsonEditor';

export interface JsonEditorProps {
	data: unknown;
	onSave?: (data: unknown) => void;
	onCancel?: () => void;
	readOnly?: boolean;
	saveLabel?: string;
}

export function JsonEditor({
	data,
	onSave,
	onCancel,
	readOnly = false,
	saveLabel = 'Save',
}: JsonEditorProps) {
	const {
		state,
		searchResults,
		diffs,
		toggleExpand,
		setEditing,
		commitEdit,
		renameKey,
		commitAndTab,
		deletePath,
		toggleNumeric,
		addKey,
		addElement,
		setSortAlpha,
		setSearchQuery,
		navigateToResult,
		clearFocus,
		reset,
	} = useJsonEditor(data);

	useEffect(() => {
		reset(data);
	}, [data, reset]);

	useEffect(() => {
		if (state.focusedPath) {
			const timer = setTimeout(() => clearFocus(), 2000);
			return () => clearTimeout(timer);
		}
	}, [state.focusedPath, clearFocus]);

	const [rawView, setRawView] = useState(false);
	const [rawText, setRawText] = useState('');
	const [rawParseError, setRawParseError] = useState<string | null>(null);

	const formattedJson = useMemo(
		() => JSON.stringify(state.workingData, null, 2),
		[state.workingData],
	);

	useEffect(() => {
		if (rawView) {
			setRawText(formattedJson);
			setRawParseError(null);
		}
	}, [rawView, formattedJson]);

	const handleSave = useCallback(() => {
		if (rawView && !readOnly) {
			if (rawParseError) return;
			try {
				onSave?.(JSON.parse(rawText));
			} catch {
				return;
			}
			return;
		}
		onSave?.(state.workingData);
	}, [onSave, state.workingData, rawView, rawText, readOnly, rawParseError]);

	const handleCancelEdit = useCallback(() => {
		setEditing(null);
	}, [setEditing]);

	return (
		<div
			className="flex h-full flex-col overflow-hidden rounded-lg border
				border-gray-200 bg-white"
		>
			{/* Toolbar */}
			<div
				className="flex items-center gap-2 border-b border-gray-200 px-3 py-2"
			>
				<SearchPanel
					searchResults={searchResults}
					onQueryChange={setSearchQuery}
					onNavigate={navigateToResult}
				/>

				<div className="mx-1 h-4 w-px shrink-0 bg-gray-200" />

				{/* Sort toggle */}
				<button
					className={`shrink-0 rounded px-1.5 py-0.5 text-xs transition-colors
						${
							state.sortAlpha
								? 'bg-blue-50 text-blue-600'
								: 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
						}`}
					onClick={() => setSortAlpha(true)}
					title="Sort keys alphabetically"
				>
					ABC
				</button>
				<button
					className={`shrink-0 rounded px-1.5 py-0.5 text-xs transition-colors
						${
							!state.sortAlpha
								? 'bg-blue-50 text-blue-600'
								: 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
						}`}
					onClick={() => setSortAlpha(false)}
					title="Insertion order"
				>
					&#x1f552;
				</button>

				<div className="mx-1 h-4 w-px shrink-0 bg-gray-200" />

				{/* Raw JSON toggle */}
				<button
					className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-xs
						whitespace-nowrap transition-colors ${
							rawView && rawParseError
								? 'cursor-not-allowed bg-red-50 text-red-400'
								: rawView
									? 'bg-blue-50 text-blue-600'
									: 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
						}`}
					onClick={() => {
						if (rawView && rawParseError) return;
						if (rawView && !readOnly) {
							try {
								reset(JSON.parse(rawText));
							} catch {
								return;
							}
						}
						setRawView((v) => !v);
					}}
					disabled={rawView && !!rawParseError}
					title={
						rawView && rawParseError
							? 'Fix JSON errors before switching views'
							: rawView
								? 'Tree view'
								: 'Raw JSON'
					}
				>
					{'{ }'}
				</button>

				<div className="flex-1" />

				{/* Cancel */}
				{!readOnly && onCancel && (
					<button
						className="rounded bg-gray-100 px-3 py-1 text-xs text-gray-700
							hover:bg-gray-200"
						onClick={onCancel}
					>
						Cancel
					</button>
				)}

				{/* Save / Send */}
				{!readOnly && onSave && (
					<button
						className={`rounded px-3 py-1 text-xs text-white ${
							rawView && rawParseError
								? 'cursor-not-allowed bg-gray-400'
								: 'bg-blue-600 hover:bg-blue-700'
						}`}
						onClick={handleSave}
						disabled={rawView && !!rawParseError}
					>
						{saveLabel}
					</button>
				)}
			</div>

			{rawView ? (
				<div className="flex flex-1 flex-col overflow-hidden">
					<textarea
						className="flex-1 resize-none bg-gray-50 p-3 font-mono text-xs
							text-gray-900 outline-none"
						value={readOnly ? formattedJson : rawText}
						readOnly={readOnly}
						onChange={
							readOnly
								? undefined
								: (e) => {
										const text = e.target.value;
										setRawText(text);
										try {
											JSON.parse(text);
											setRawParseError(null);
										} catch (err) {
											setRawParseError(
												err instanceof SyntaxError
													? err.message
													: 'Invalid JSON',
											);
										}
									}
						}
						spellCheck={false}
					/>
					{rawParseError && (
						<div
							className="border-t border-red-200 bg-red-50 px-3 py-1.5 text-xs
								text-red-600"
						>
							{rawParseError}
						</div>
					)}
				</div>
			) : (
				<>
					{/* Tree */}
					<div className="flex-1 overflow-y-auto p-2">
						<JsonNode
							value={state.workingData}
							path=""
							depth={0}
							keyEditable={false}
							isExpanded={state.expandedPaths.has('')}
							isFocused={state.focusedPath === ''}
							sortAlpha={state.sortAlpha}
							readOnly={readOnly}
							expandedPaths={state.expandedPaths}
							editingPath={state.editingPath}
							editingTarget={state.editingTarget}
							focusedPath={state.focusedPath}
							onToggleExpand={toggleExpand}
							onStartEdit={setEditing}
							onCommitEdit={commitEdit}
							onRenameKey={renameKey}
							onCommitAndTab={commitAndTab}
							onCancelEdit={handleCancelEdit}
							onDelete={deletePath}
							onToggleNumeric={toggleNumeric}
							onAddKey={addKey}
							onAddElement={addElement}
						/>
					</div>

					{/* Diff panel */}
					{!readOnly && <DiffView diffs={diffs} />}
				</>
			)}
		</div>
	);
}
